import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Send } from "lucide-react";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    serviceInterest: "",
    subject: "",
    message: "",
    recipient: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to send");

      toast({
        title: "Message Sent Successfully!",
        description: "Expect to hear back from us soonest.",
        variant: "success",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        organization: "",
        serviceInterest: "",
        subject: "",
        message: "",
        recipient: "",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Something went wrong. Please try again. Error: ${
          error.message || error
        }`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium text-foreground">
            Full Name *
          </label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            required
            placeholder="Enter your full name"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="email"
            className="text-sm font-medium text-foreground"
          >
            Email Address *
          </label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            required
            placeholder="Enter your email address"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="phone"
            className="text-sm font-medium text-foreground"
          >
            Phone Number
          </label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="Enter your phone number"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="organization"
            className="text-sm font-medium text-foreground"
          >
            Organization
          </label>
          <Input
            id="organization"
            value={formData.organization}
            onChange={(e) => handleChange("organization", e.target.value)}
            placeholder="Enter your organization name"
          />
        </div>
         {/* Contact Purpose Selector */}
      <div className="space-y-2">
        <label
          htmlFor="recipient"
          className="text-sm font-medium text-foreground"
        >
          Contact Purpose *
        </label>
        <Select
          value={formData.recipient}
          onValueChange={(value) => handleChange("recipient", value)}
          required
        >
          <SelectTrigger>
            <SelectValue placeholder="Choose who to contact" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="info@tcla-x.com">
              General Infos & Services
            </SelectItem>
            <SelectItem value="director@tcla-x.com">
              Administrative / Partnerships
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label
          htmlFor="subject"
          className="text-sm font-medium text-foreground"
        >
          Subject *
        </label>
        <Input
          id="subject"
          value={formData.subject}
          onChange={(e) => handleChange("subject", e.target.value)}
          required
          placeholder="Enter a brief subject for your message"
        />
      </div>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="serviceInterest"
          className="text-sm font-medium text-foreground"
        >
          Service Interest
        </label>
        <Select
          value={formData.serviceInterest}
          onValueChange={(value) => handleChange("serviceInterest", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a service category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="leadership">Leadership Development</SelectItem>
            <SelectItem value="project-management">
              Project Management Excellence
            </SelectItem>
            <SelectItem value="management">Management Excellence</SelectItem>
            <SelectItem value="professional">
              Professional Development
            </SelectItem>
            <SelectItem value="communication">
              Communication Excellence
            </SelectItem>
            <SelectItem value="customer">Customer Excellence</SelectItem>
            <SelectItem value="wellness">Wellness & Integration</SelectItem>
            <SelectItem value="coaching">
              Performance & Career Coaching
            </SelectItem>
            <SelectItem value="digital">Digital Transformation</SelectItem>
            <SelectItem value="custom">Custom Training Solution</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="message"
          className="text-sm font-medium text-foreground"
        >
          Message *
        </label>
        <Textarea
          id="message"
          value={formData.message}
          onChange={(e) => handleChange("message", e.target.value)}
          required
          placeholder="Tell us about your training needs or ask any questions..."
          rows={5}
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full md:w-auto"
        size="lg"
      >
        {isSubmitting ? (
          "Sending..."
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
